<script lang="ts">
import type { ResolvedMonospaceCollection } from '#layers/data-model/types/schema/resolved-schema';
import { getCollectionPrimaryKeyFields } from '#layers/data-model/utils/schema/collection';

export function initialSortState(collection: ResolvedMonospaceCollection) {
	const primaryKeyFields = getCollectionPrimaryKeyFields(collection);

	if (primaryKeyFields.length > 0) {
		return [{ fieldId: primaryKeyFields[0]!.id, direction: 'asc' as const }];
	}

	return [];
}
</script>

<script setup lang="ts">
import type { LayoutSortingState } from '~/types/layout';

import { computed } from 'vue';

defineProps<{
	collection: ResolvedMonospaceCollection;
}>();

const modelValue = defineModel<LayoutSortingState>({ default: () => [] });

// SortingState is an array, but we only support single-column sorting here
const sort = computed({
	get: () => modelValue.value?.[0],
	set: (value) => {
		if (value) {
			modelValue.value = [value];
		}
		else {
			modelValue.value = [];
		}
	},
});

const sortField = computed({
	get: () => sort.value?.fieldId ?? null,
	set: (value) => {
		if (value) {
			sort.value = { fieldId: value, direction: 'asc' };
		}
		else {
			sort.value = undefined;
		}
	},
});

const sortDirection = computed({
	get: () => sort.value?.direction ?? 'asc',
	set: (value) => {
		if (!sort.value) return;
		sort.value = { ...sort.value, direction: value };
	},
});
</script>

<template>
	<SortFormField
		v-model:direction="sortDirection"
		:show-toggle="!!sortField"
	>
		<template #field-select>
			<CollectionFieldSelect
				v-model="sortField"
				include-primitive-fields
				:collection-id="collection.id"
				:include-relation-fields="false"
				:include-constrained-fields="false"
				:clear="false"
				class="w-full"
			/>
		</template>
	</SortFormField>
</template>
